import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMap } from "@vis.gl/react-google-maps";
import { ArrowLeft, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { MapMarker } from "@/components/map/MapMarker";
import { SportCourtMarker, getCourtColor } from "@/components/map/SportCourtMarker";
import { SearchRadiusOverlay } from "@/components/map/SearchRadiusOverlay";
import { PILMarker } from "@/components/physical-mapping/PILMarker";
import { PILPlacementToolbar } from "@/components/physical-mapping/PILPlacementToolbar";
import { AdminPILForm } from "@/components/physical-mapping/AdminPILForm";
import type { AdminPILFormData } from "@/components/physical-mapping/AdminPILForm";
import { LocationMappingFieldsForm } from "@/components/physical-mapping/LocationMappingFieldsForm";
import { getEffectiveRadius, MIN_RADIUS, MAX_RADIUS } from "@/lib/sport-defaults";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface MapContentProps {
  location: { id: string; lat: number; lng: number; name: string; primary_type: string | null };
  pils: AdminPILFormData[];
  courts: Array<{ id: string; lat: number; lng: number; sport_type: string; search_radius_m: number | null }>;
  selectedPilId: string | null;
  isPlacing: boolean;
  onPlacePIL: (lat: number, lng: number) => void;
  onPilMoved: (pilId: string, lat: number, lng: number) => void;
  onPilClick: (pilId: string) => void;
}

function MapContent({
  location,
  pils,
  courts,
  selectedPilId,
  isPlacing,
  onPlacePIL,
  onPilMoved,
  onPilClick,
}: MapContentProps) {
  const map = useMap();

  useEffect(() => {
    if (!map || !isPlacing) return;
    const listener = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        onPlacePIL(e.latLng.lat(), e.latLng.lng());
      }
    });
    return () => listener.remove();
  }, [map, isPlacing, onPlacePIL]);

  useEffect(() => {
    if (!map) return;
    map.setOptions({ draggableCursor: isPlacing ? "crosshair" : "" });
    return () => {
      map.setOptions({ draggableCursor: "" });
    };
  }, [map, isPlacing]);

  useEffect(() => {
    if (!map || !selectedPilId) return;
    const pil = pils.find((p) => p.id === selectedPilId);
    if (!pil) return;
    map.panTo({ lat: pil.lat, lng: pil.lng });
    const currentZoom = map.getZoom() ?? 0;
    if (currentZoom < 20) map.setZoom(20);
  }, [map, selectedPilId, pils]);

  return (
    <>
      <MapMarker
        locationId={location.id}
        position={{ lat: location.lat, lng: location.lng }}
        hasSportField={null}
        primaryType={location.primary_type}
        name={location.name}
        isMuted
        isReviewed={false}
        isRejected={false}
      />
      <SearchRadiusOverlay
        courts={courts.map((c) => ({
          id: c.id,
          lat: c.lat,
          lng: c.lng,
          radius: getEffectiveRadius(c.sport_type, c.search_radius_m),
        }))}
      />
      {courts.map((court) => (
        <SportCourtMarker
          key={court.id}
          courtId={court.id}
          position={{ lat: court.lat, lng: court.lng }}
          sportType={court.sport_type}
        />
      ))}
      {pils.map((pil) => (
        <PILMarker
          key={pil.id}
          pil={pil}
          isSelected={pil.id === selectedPilId}
          isDraggable
          onDragEnd={(lat, lng) => onPilMoved(pil.id, lat, lng)}
          onClick={onPilClick}
        />
      ))}
    </>
  );
}

function CourtRadiusRow({ court, locationId }: { court: { id: string; sport_type: string; search_radius_m: number | null }; locationId: string }) {
  const utils = trpc.useUtils();
  const [radius, setRadius] = useState(getEffectiveRadius(court.sport_type, court.search_radius_m));

  const updateMutation = trpc.sportCourts.update.useMutation({
    onSuccess: () => utils.sportCourts.listByLocation.invalidate({ locationId }),
    onError: (e) => toast.error(`Failed to update radius: ${e.message}`),
  });

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: getCourtColor(court.sport_type) }} />
        <span className="text-sm font-medium capitalize">{court.sport_type.replace(/_/g, " ")}</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">{radius}m</span>
      </div>
      <Slider
        min={MIN_RADIUS}
        max={MAX_RADIUS}
        step={5}
        value={[radius]}
        onValueChange={([v]) => setRadius(v!)}
        onValueCommit={([v]) => updateMutation.mutate({ id: court.id, searchRadiusM: v! })}
      />
    </div>
  );
}

export function PhysicalMappingLocationPage() {
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false }) as { collectionId?: string };
  const navigate = useNavigate();
  const locationId = (params as { locationId?: string }).locationId!;
  const collectionId = search.collectionId;

  const [isPlacing, setIsPlacing] = useState(false);
  const [selectedPilId, setSelectedPilId] = useState<string | null>(null);
  const pilRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const utils = trpc.useUtils();

  const { data: details, isLoading } =
    trpc.physicalMapping.getLocationDetails.useQuery(
      { locationId },
      { enabled: !!locationId },
    );

  // Neighbor list for J/K nav
  const { data: neighbors } = trpc.physicalMapping.list.useQuery(
    { collectionId: collectionId ?? "" },
    { enabled: !!collectionId },
  );

  const navTo = (opts: { to: string; params?: Record<string, string>; search?: Record<string, unknown> }) =>
    navigate(opts as any);

  const { data: courts } = trpc.sportCourts.listByLocation.useQuery(
    { locationId },
    { enabled: !!locationId },
  );

  const createPilMutation = trpc.physicalMapping.createPIL.useMutation({
    onSuccess: (pil: any) => {
      utils.physicalMapping.getLocationDetails.invalidate({ locationId });
      utils.physicalMapping.list.invalidate();
      if (pil?.id) setSelectedPilId(pil.id);
      setIsPlacing(false);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const updatePilMutation = trpc.physicalMapping.updatePIL.useMutation({
    onSuccess: () => {
      utils.physicalMapping.getLocationDetails.invalidate({ locationId });
    },
    onError: (e: { message: string }) => toast.error(`Move failed: ${e.message}`),
  });

  const handlePlacePIL = useCallback(
    (lat: number, lng: number) => {
      createPilMutation.mutate({ locationId, lat, lng });
    },
    [createPilMutation, locationId],
  );

  const handlePilMoved = useCallback(
    (pilId: string, lat: number, lng: number) => {
      updatePilMutation.mutate({ id: pilId, patch: { lat, lng } });
    },
    [updatePilMutation],
  );

  const handlePilClick = useCallback((pilId: string) => {
    setSelectedPilId(pilId);
    const el = pilRefs.current.get(pilId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  // Keyboard navigation J/K + arrows
  const { prev, next, nextNeedingPils } = useMemo(() => {
    if (!neighbors || neighbors.length === 0)
      return { prev: null, next: null, nextNeedingPils: null };
    const idx = neighbors.findIndex((n: any) => n.id === locationId);
    if (idx < 0) return { prev: null, next: null, nextNeedingPils: null };
    // Find first location AFTER current that still needs PILs (pil_total === 0),
    // wrapping to the start if nothing is found after the current position.
    const after = neighbors
      .slice(idx + 1)
      .find((n: any) => (n.pil_total ?? 0) === 0);
    const before = neighbors
      .slice(0, idx)
      .find((n: any) => (n.pil_total ?? 0) === 0);
    return {
      prev: idx > 0 ? neighbors[idx - 1] : null,
      next: idx < neighbors.length - 1 ? neighbors[idx + 1] : null,
      nextNeedingPils: after ?? before ?? null,
    };
  }, [neighbors, locationId]);

  const goToNextNeedingPils = useCallback(() => {
    if (nextNeedingPils) {
      navTo({
        to: "/dashboard/physical-mapping/$locationId",
        params: { locationId: nextNeedingPils.id },
        search: { collectionId },
      });
    } else {
      toast.success("All locations in this collection have installation spots 🎉");
      navTo({
        to: "/dashboard/physical-mapping",
        search: collectionId ? { collectionId } : {},
      });
    }
  }, [nextNeedingPils, collectionId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if (e.key === "j" || e.key === "ArrowDown") {
        if (next) {
          navTo({
            to: "/dashboard/physical-mapping/$locationId",
            params: { locationId: next.id },
            search: { collectionId },
          });
        }
      } else if (e.key === "k" || e.key === "ArrowUp") {
        if (prev) {
          navTo({
            to: "/dashboard/physical-mapping/$locationId",
            params: { locationId: prev.id },
            search: { collectionId },
          });
        }
      } else if (e.key === "s" || e.key === "S") {
        goToNextNeedingPils();
      } else if (e.key === "Escape" && isPlacing) {
        setIsPlacing(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [next, prev, navigate, collectionId, isPlacing, goToNextNeedingPils]);

  if (isLoading || !details) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-64 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-[500px]" />
          <Skeleton className="h-[500px]" />
        </div>
      </div>
    );
  }

  const location = details.location;
  const pils: AdminPILFormData[] = (details.pils ?? []) as AdminPILFormData[];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-4 py-2 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navTo({
              to: "/dashboard/physical-mapping",
              search: collectionId ? { collectionId } : {},
            })
          }
        >
          <ArrowLeft className="size-4 mr-1" />
          Back
        </Button>
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-base font-semibold truncate">{location.name}</h1>
          <Badge variant="outline" className="text-xs">
            {(location.mapping_status ?? "not_started").replace("_", " ")}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {pils.length} PIL{pils.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!prev}
            onClick={() =>
              prev &&
              navTo({
                to: "/dashboard/physical-mapping/$locationId",
                params: { locationId: prev.id },
                search: { collectionId },
              })
            }
            title="Previous (K)"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!next}
            onClick={() =>
              next &&
              navTo({
                to: "/dashboard/physical-mapping/$locationId",
                params: { locationId: next.id },
                search: { collectionId },
              })
            }
            title="Next (J)"
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            size="sm"
            className="ml-2"
            onClick={goToNextNeedingPils}
            title="Save & jump to next location needing installation spots (S)"
          >
            <Check className="size-4 mr-1" />
            {nextNeedingPils ? "Save & next" : "Save & finish"}
          </Button>
        </div>
      </div>

      {/* Split layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Map */}
        <div className="flex-1 relative border-r">
          <GoogleMapWrapper
            key={locationId}
            defaultCenter={{ lat: location.lat, lng: location.lng }}
            defaultZoom={20}
            mapTypeId="hybrid"
            fullscreenControl={false}
          >
            <MapContent
              location={location}
              pils={pils}
              courts={courts ?? []}
              selectedPilId={selectedPilId}
              isPlacing={isPlacing}
              onPlacePIL={handlePlacePIL}
              onPilMoved={handlePilMoved}
              onPilClick={handlePilClick}
            />
          </GoogleMapWrapper>

          {/* Toolbar overlay */}
          <div className="absolute top-3 left-3 right-3 z-10 flex justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <PILPlacementToolbar
                isPlacing={isPlacing}
                onStart={() => setIsPlacing(true)}
                onCancel={() => setIsPlacing(false)}
              />
            </div>
          </div>
        </div>

        {/* Right pane */}
        <div className="w-[32rem] shrink-0 overflow-y-auto p-4 space-y-4 bg-muted/20">
          <LocationMappingFieldsForm
            locationId={location.id}
            publicAccess={location.mapping_public_access ?? null}
            open247={location.mapping_open_24_7 ?? null}
            openingHours={location.mapping_opening_hours ?? null}
            passingByVisibility={location.mapping_passing_by_visibility ?? null}
            nearbyHabitations={location.mapping_nearby_habitations ?? null}
            signsOfDegradation={location.mapping_signs_of_degradation ?? null}
            janitors={location.mapping_janitors ?? null}
            lighting={location.mapping_lighting_present ?? null}
            dubiousPeople={location.mapping_dubious_people ?? null}
            videoCameras={location.mapping_video_cameras ?? null}
            locationPhotos={(details.location_photos ?? []).map((p: any) => ({
              id: p.id,
              kind: p.kind,
              url: p.url,
            }))}
            minInstallationSpots={location.min_installation_spots ?? 1}
            placedPinCount={pils.filter((p: any) => p.status !== "rejected").length}
          />

          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
              Installation spots
            </h3>
            {pils.length === 0 ? (
              <div
                className={cn(
                  "rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground",
                  isPlacing && "border-primary/40 bg-primary/5",
                )}
              >
                {isPlacing
                  ? "Click the map to drop the first installation spot."
                  : 'No installation spots yet. Click "Add installation spot" above the map to start.'}
              </div>
            ) : (
              pils.map((pil) => (
                <AdminPILForm
                  key={pil.id}
                  ref={(el) => {
                    if (el) pilRefs.current.set(pil.id, el);
                    else pilRefs.current.delete(pil.id);
                  }}
                  pil={pil}
                  locationId={location.id}
                  isSelected={pil.id === selectedPilId}
                  onSelect={() => setSelectedPilId(pil.id)}
                />
              ))
            )}
          </div>

          {(courts ?? []).length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
                Sport Courts
              </h3>
              {(courts ?? []).map((court) => (
                <CourtRadiusRow key={court.id} court={court} locationId={location.id} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
